import React, { useState, useEffect } from 'react';
import { ShoppingCart, Search, Trash2, CreditCard } from 'lucide-react';
import api from '../services/api';
import ReceiptModal from '../components/ReceiptModal';

const POS = () => {
    const [products, setProducts] = useState<any[]>([]);
    const [cart, setCart] = useState<any[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [receiptData, setReceiptData] = useState<any>(null);

    useEffect(() => {
        // Fetch products on mount
        const fetchProducts = async () => {
            try {
                const res = await api.get('/products');
                setProducts(res.data);
            } catch (err) {
                console.error(err);
            }
        };
        fetchProducts();
    }, []);

    const addToCart = (product: any) => {
        setCart(prev => {
            const existing = prev.find(item => item.id === product.id);
            if (existing) {
                if (existing.quantity >= product.stock_quantity) return prev; // pas de stock
                return prev.map(item => item.id === product.id ? { ...item, quantity: item.quantity + 1 } : item);
            }
            return [...prev, { ...product, quantity: 1 }];
        });
    };

    const removeFromCart = (id: number) => {
        setCart(prev => prev.filter(item => item.id !== id));
    };

    const updateQuantity = (id: number, delta: number) => {
        setCart(prev => prev.map(item => {
            if (item.id === id) {
                const newQuantity = item.quantity + delta;
                if (newQuantity > 0 && newQuantity <= item.stock_quantity) {
                    return { ...item, quantity: newQuantity };
                }
            }
            return item;
        }));
    };

    const total = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);

    const handleCheckout = async () => {
        if (cart.length === 0) return;
        try {
            const saleData = {
                total_amount: total,
                items: cart.map(item => ({
                    product_id: item.id,
                    quantity: item.quantity,
                    unit_price: item.price
                }))
            };
            await api.post('/pos/sale', saleData);
            
            // Ouvrir le reçu
            setReceiptData({
                type: 'sale',
                data: {
                    items: cart,
                    total: total
                }
            });
            
            // Refresh products to show new stock
            const res = await api.get('/products');
            setProducts(res.data);
        } catch (error) {
            console.error('Erreur checkout', error);
            alert('Erreur lors de la validation de la vente.');
        }
    };
    
    const closeReceipt = () => {
        setReceiptData(null);
        setCart([]); // On vide le panier seulement à la fermeture du reçu
    };

    const filteredProducts = products.filter(p => p.name.toLowerCase().includes(searchTerm.toLowerCase()));

    return (
        <div className="h-full flex flex-col lg:flex-row gap-6 overflow-y-auto lg:overflow-hidden pb-6 lg:pb-0">
            {/* Left side: Catalog */}
            <div className="flex-none h-[60vh] lg:h-auto lg:flex-1 flex flex-col bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
                <div className="p-4 border-b border-gray-100">
                    <div className="relative">
                        <input 
                            type="text" 
                            placeholder="Rechercher un article..." 
                            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-md focus:ring-brand-primary focus:border-brand-primary outline-none"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        <Search className="absolute left-3 top-3.5 text-gray-400" size={20} />
                    </div>
                </div>
                
                <div className="flex-1 overflow-y-auto p-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                        {filteredProducts.map(product => (
                            <div 
                                key={product.id} 
                                onClick={() => product.stock_quantity > 0 && addToCart(product)}
                                className={`p-4 border rounded-lg flex flex-col justify-between transition-shadow ${product.stock_quantity > 0 ? 'cursor-pointer hover:shadow-md border-gray-200' : 'opacity-50 cursor-not-allowed border-gray-100'}`}
                            >
                                <div className="flex items-start gap-3">
                                    {product.image_url ? (
                                        <img src={product.image_url} alt={product.name} className="w-16 h-16 object-cover rounded-md border border-gray-100 flex-shrink-0" />
                                    ) : (
                                        <div className="w-16 h-16 bg-gray-50 rounded-md border border-gray-100 flex items-center justify-center flex-shrink-0 text-gray-300">
                                            <ShoppingCart size={24} />
                                        </div>
                                    )}
                                    <div>
                                        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{product.category}</span>
                                        <h3 className="font-medium text-gray-900 mt-1 line-clamp-2">{product.name}</h3>
                                    </div>
                                </div>
                                <div className="mt-4 flex justify-between items-end">
                                    <span className="font-black text-brand-primary text-lg">{Number(product.price).toFixed(2)} FCFA</span>
                                    <span className={`text-xs font-medium px-2 py-1 rounded ${product.stock_quantity > 0 ? 'bg-gray-100 text-gray-600' : 'bg-red-100 text-red-600'}`}>
                                        {product.stock_quantity > 0 ? `Stock: ${product.stock_quantity}` : 'Rupture'}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Right side: Cart */}
            <div className="w-full lg:w-96 flex-none h-[60vh] lg:h-auto flex flex-col bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
                <div className="p-4 bg-gray-50 border-b border-gray-100 flex items-center justify-between">
                    <h2 className="text-lg font-bold text-brand-dark flex items-center gap-2">
                        <ShoppingCart size={20} />
                        Panier ({cart.length})
                    </h2>
                    {cart.length > 0 && (
                        <button onClick={() => setCart([])} className="text-sm text-red-500 hover:underline">Vider</button>
                    )}
                </div>

                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                    {cart.length === 0 ? (
                        <div className="h-full flex flex-col items-center justify-center text-gray-400">
                            <ShoppingCart size={48} className="mb-2 opacity-50" />
                            <p>Le panier est vide</p>
                        </div>
                    ) : (
                        cart.map(item => (
                            <div key={item.id} className="flex gap-3 items-center border-b border-gray-50 pb-3">
                                <div className="flex-shrink-0">
                                    {item.image_url ? (
                                        <img src={item.image_url} alt={item.name} className="w-10 h-10 object-cover rounded border border-gray-100" />
                                    ) : (
                                        <div className="w-10 h-10 bg-gray-100 rounded flex items-center justify-center text-gray-400">
                                            <ShoppingCart size={16} />
                                        </div>
                                    )}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <h4 className="font-medium text-sm text-gray-900 truncate">{item.name}</h4>
                                    <p className="text-brand-primary font-semibold text-sm">{Number(item.price).toFixed(2)} FCFA</p>
                                </div>
                                <div className="flex items-center gap-2">
                                    <button onClick={() => updateQuantity(item.id, -1)} className="w-7 h-7 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded text-gray-700 font-bold">-</button>
                                    <span className="w-6 text-center font-medium text-sm">{item.quantity}</span>
                                    <button onClick={() => updateQuantity(item.id, 1)} className="w-7 h-7 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded text-gray-700 font-bold">+</button>
                                </div>
                                <button onClick={() => removeFromCart(item.id)} className="text-red-400 hover:text-red-600 p-1">
                                    <Trash2 size={16} />
                                </button>
                            </div>
                        ))
                    )}
                </div>

                <div className="p-4 border-t border-gray-100 bg-gray-50">
                    <div className="flex justify-between items-center mb-4">
                        <span className="text-gray-600 font-medium">Total</span>
                        <span className="text-2xl font-black text-brand-dark">{total.toFixed(2)} FCFA</span>
                    </div>
                    <button 
                        onClick={handleCheckout}
                        disabled={cart.length === 0}
                        className={`w-full py-3 flex items-center justify-center gap-2 rounded-md font-bold text-white transition-colors ${
                            cart.length > 0 ? 'bg-brand-accent hover:bg-emerald-600' : 'bg-gray-300 cursor-not-allowed'
                        }`}
                    >
                        <CreditCard size={20} />
                        Encaisser
                    </button>
                </div>
            </div>
            
            {receiptData && (
                <ReceiptModal
                    isOpen={!!receiptData}
                    onClose={closeReceipt}
                    type={receiptData.type}
                    data={receiptData.data}
                />
            )}
        </div>
    );
};

export default POS;
